import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Badge } from "react-bootstrap";
import {
  fetchBanners,
  deleteBanner,
  clearError,
  clearSuccess,
} from "../../redux/slices/bannerSlice";
import BannerModal from "./BannerModal";
import InfoPill from "../Common/InfoPill";
import ConfirmModal from "../Common/ConfirmModal";
import { FaPencilAlt, FaRegTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { formatDateToDDMMYYYY } from "../../utils/dateUtils";

const Banners = () => {
  const dispatch = useDispatch();
  const {
    banners = [],
    isLoading,
    error,
    success,
    isDeleting,
    isUpdating,
  } = useSelector((state) => state.banners);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState(null);

  useEffect(() => {
    dispatch(fetchBanners());
  }, [dispatch]);

  // Clear messages after a delay (toasts are handled in modals and action handlers)
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => dispatch(clearError()), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => dispatch(clearSuccess()), 5000);
      return () => clearTimeout(timer);
    }
  }, [success, dispatch]);

  const handleEditBanner = (bannerId) => {
    setEditingBanner(bannerId);
    setShowModal(true);
  };

  const handleCreateBanner = () => {
    setEditingBanner(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingBanner(null);
    // Refresh banners list after modal closes
    dispatch(fetchBanners());
  };

  const handleDeleteBanner = (bannerId, bannerName) => {
    setBannerToDelete({ id: bannerId, name: bannerName });
    setShowConfirmModal(true);
  };

  const confirmDeleteBanner = async () => {
    if (bannerToDelete) {
      try {
        await dispatch(deleteBanner(bannerToDelete.id)).unwrap();
        toast.success(`Banner "${bannerToDelete.name}" deleted successfully`);
        // Refresh banners list after successful deletion
        dispatch(fetchBanners());
        setShowConfirmModal(false);
        setBannerToDelete(null);
      } catch (error) {
        console.error("Failed to delete banner:", error);
        toast.error(
          error?.message || `Failed to delete banner "${bannerToDelete.name}"`
        );
      }
    }
  };

  const cancelDeleteBanner = () => {
    setShowConfirmModal(false);
    setBannerToDelete(null);
  };

  // Table headers - similar to categories page
  const headers = [
    {
      prop: "name",
      title: "Banner Name",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <span className="text-decoration-none fw-bold text-primary">
          {row.name}
        </span>
      ),
    },
    {
      prop: "image_urls",
      title: "Banner Images",
      cell: (row) => {
        const images = row.image_urls || [];
        if (images.length === 0) {
          return <span className="text-muted">No images</span>;
        }
        return (
          <div className="d-flex gap-1">
            {images.slice(0, 3).map((url, index) => (
              <img
                key={index}
                src={url}
                alt={`${row.name} ${index + 1}`}
                style={{
                  width: "40px",
                  height: "40px",
                  objectFit: "cover",
                  borderRadius: "4px",
                  border: "1px solid #ddd",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ))}
            {images.length > 3 && (
              <span className="badge bg-secondary">+{images.length - 3}</span>
            )}
          </div>
        );
      },
    },
    {
      prop: "status",
      title: "Status",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <Badge bg={row.status === "active" ? "success" : "secondary"}>
          {row.status === "active" ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      prop: "created_at",
      title: "Created Date",
      isSortable: true,
      cell: (row) => formatDateToDDMMYYYY(row.created_at) || "-",
    },
    {
      prop: "actions",
      title: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            size="sm"
            variant="outline-warning"
            onClick={() => handleEditBanner(row.id)}
            disabled={isUpdating || isDeleting}
          >
            <FaPencilAlt />
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => handleDeleteBanner(row.id, row.name)}
            disabled={isUpdating || isDeleting}
          >
            <FaRegTrashAlt />
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <div className="text-center mt-4">Loading banners...</div>;
  }

  const labels = {
    beforeSelect: " ",
  };

  return (
    <div className="page-container">
      <BannerModal
        show={showModal}
        handleClose={handleCloseModal}
        editId={editingBanner}
      />

      <ConfirmModal
        show={showConfirmModal}
        onHide={cancelDeleteBanner}
        onConfirm={confirmDeleteBanner}
        title="Delete Banner"
        message={`Are you sure you want to delete the banner "${bannerToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Banners
        </Link>
      </div>

      <DatatableWrapper
        body={banners}
        headers={headers}
        paginationOptionsProps={{
          initialState: { rowsPerPage: 5, options: [5, 10, 15, 20] },
        }}
      >
        <div className="compact-controls d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
          <div className="d-flex align-items-center">
            <div className="flex-grow-1 me-2" style={{ maxWidth: "300px" }}>
              <Filter placeholder="Search banners..." className="w-100" />
            </div>
            <PaginationOptions labels={labels} />
            <InfoPill left="Total Banners" right={banners?.length} />
          </div>

          <div className="d-flex align-items-center">
            <Button
              onClick={handleCreateBanner}
              variant="success"
              className="btn-sm"
            >
              New Banner
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="table-responsive shadow-sm">
          <table className="table table-striped table-bordered align-middle">
            <TableHeader />
            <TableBody />
          </table>
        </div>

        {/* Pagination */}
        <div className="compact-pagination d-flex justify-content-left mt-3">
          <Pagination classes={{
            button: "bg-dark",
          }} />
        </div>
      </DatatableWrapper>
    </div>
  );
};

export default Banners;
